import { HttpEventType } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomAttachFilesComponent } from 'src/app/components/custom-attach-files/custom-attach-files.component';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { BranchModel } from 'src/app/models/management/branch.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-advanced-options-branch',
  templateUrl: './advanced-options-branch.component.html',
  styleUrls: ['./advanced-options-branch.component.scss'],
  standalone: false,
})
export class AdvancedOptionsBranchComponent implements OnInit {
  @ViewChild('attachFile') attachFileComponent?: CustomModalComponent | null = null;

  branchContract: BranchModel = new BranchModel();
  componentAttachFiles = CustomAttachFilesComponent;
  disabledForm: boolean = false;
  formBranch: FormGroup = new FormGroup({});
  isRentalSale: boolean = true;
  nameComponent: string = 'Condiciones Comerciales Alquiler';
  subscription: Subscription;
  permissionUpdatable: boolean = false;
  listStore: any[] = [];
  listFiles: any[] = [];
  inputsForAttachFiles$ = new BehaviorSubject<any>({
    listFiles: [],
    labelConsecutive: 0,
    disabled: false
  });
  outputForAttachFiles$ = new Subject<any>();


  constructor(private branchService: BranchService,
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    private confirmDialogService: ConfirmDialogService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new BranchModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.branchContract = inputs.branchContract;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.disabledForm = inputs.disabledForm;
      this.listStore = inputs.listStore;
      this.validateForm(this.branchContract);
    });

    this.subscription = this.outputForAttachFiles$.subscribe(async ({ type, payload }) => {
      switch (type) {
        case 'uploadFile':
          this.uploadFile(payload);
          break;
        case 'downloadFile':
          this.downloadFile(payload);
          break;
        case 'deleteFile':
          this.deleteFile();
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async deleteFile() {
    if (this.branchContract.branchId != 0) {
      const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar el adjunto seleccionado?');
      if (accepted) {
        try {
          this.branchContract = await firstValueFrom(this.branchService.deleteAttachConditions(this.branchContract.branchId, this.isRentalSale));
          this.validateForm(this.branchContract);
          this.toastHandlerService.showToast('success', 'El adjunto fue eliminado exitosamente', '');
          this.getConditionsFiles(this.isRentalSale);
        } catch (error) {
          console.error('Error catch:', error);
        }
      }
    }
  }

  downloadFile(fileName: any) {
    if (this.branchContract.branchId != 0) {
      this.branchService.downloadConditions(this.branchContract.branchId, this.isRentalSale).subscribe(data => {
        switch (data.type) {
          case HttpEventType.Response: {
            const downloadedFile = new Blob([data.body!], { type: data.body!.type });
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.download = fileName;
            a.href = URL.createObjectURL(downloadedFile);
            a.target = '_blank';
            a.click();
            document.body.removeChild(a);
            break;
          }
        }
      });
    }
  }

  getBranchModel(): BranchModel {
    const model = this.formBranch.getRawValue() as BranchModel;
    const modelRecord = model as Record<string, any>;
    const fieldsToNullify = [
      'predevolutionObservation',
      'devolutionObservation',
      'remissionObservation',
      'icaInvoiceObservation',
      'reteIvaSaleCode',
      'cxcAccount',
      'creeDebitAccount',
      'creeCreditAccount',
      'utilizationAccount',
      'canceledInvoiceAccount',
      'utilityIvaContractAccount',
      'invoiceVoucherCode',
      'debitNoteVoucherCode',
      'creditNoteVoucherCode',
      'cashReceiptVoucherCode',
      'invoiceVoucherType',
      'creditNoteVoucherType',
      'debitNoteVoucherType',
      'cashReceiptVoucherType',
      'particularConditionRental',
      'particularConditionSale',
      'quotationObservation'
    ];

    fieldsToNullify.forEach(field => {
      if (String(modelRecord[field] ?? '').trim() === '') {
        modelRecord[field] = null;
      }
    });
    return model;
  }

  async send() {
    this.validateFormControls();
    if (!this.formBranch.valid) {
      this.showValidationMessages();
      return;
    }
    const modelBranch = this.getBranchModel();
    const { isValid, model } = this.validateStoreSelection(modelBranch);
    if (isValid) {
      try {
        this.updateBranchContract(model);
        await firstValueFrom(this.branchService.updateBranch(this.branchContract));
        this.toastHandlerService.showToast('success', 'La sucursal fue modificada exitosamente.');
      } catch (error) {
        console.error('Error al actualizar la sucursal.', error);
      }
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      icaInvoiceObservation: {
        maxlength: 'La Observación ICA no puede superar 250 caracteres.'
      },
      reteIvaSaleCode: {
        maxlength: 'El Código de Rete IVA no puede superar 10 caracteres.'
      },
      cxcAccount: {
        maxlength: 'La Cuenta CxC no puede superar 10 caracteres.'
      },
      creeDebitAccount: {
        maxlength: 'La Cuenta CREE Débito no puede superar 10 caracteres.'
      },
      creeCreditAccount: {
        maxlength: 'La Cuenta CREE Crédito no puede superar 10 caracteres.'
      },
      utilizationAccount: {
        maxlength: 'La Cuenta de Utilización no puede superar 10 caracteres.'
      },
      canceledInvoiceAccount: {
        maxlength: 'La Cuenta de Facturas Anuladas no puede superar 10 caracteres.'
      },
      utilityIvaContractAccount: {
        maxlength: 'La Cuenta de Utilidad IVA por Contrato no puede superar 10 caracteres.'
      },
      invoiceVoucherCode: {
        maxlength: 'El Código de Comprobante de Factura no puede superar 6 caracteres.'
      },
      debitNoteVoucherCode: {
        maxlength: 'El Código de Comprobante de Nota Débito no puede superar 6 caracteres.'
      },
      creditNoteVoucherCode: {
        maxlength: 'El Código de Comprobante de Nota Crédito no puede superar 6 caracteres.'
      },
      cashReceiptVoucherCode: {
        maxlength: 'El Código de Recibo de Caja no puede superar 6 caracteres.'
      },
      invoiceVoucherType: {
        maxlength: 'El Tipo de Comprobante de Factura no puede superar 6 caracteres.'
      },
      creditNoteVoucherType: {
        maxlength: 'El Tipo de Comprobante de Nota Crédito no puede superar 6 caracteres.'
      },
      debitNoteVoucherType: {
        maxlength: 'El Tipo de Comprobante de Nota Débito no puede superar 6 caracteres.'
      },
      cashReceiptVoucherType: {
        maxlength: 'El Tipo de Comprobante de Recibo de Caja no puede superar 6 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formBranch.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  updateInputsForAttachFiles() {
    this.inputsForAttachFiles$.next({
      listFiles: this.listFiles,
      labelConsecutive: this.branchContract.code,
      disabled: this.disabledForm
    });
  }

  async getConditionsFiles(isRentalSale: boolean) {
    try {
      this.listFiles = await firstValueFrom(this.branchService.getConditionsFilesAsync(this.branchContract.branchId, isRentalSale));
      this.updateInputsForAttachFiles();
    } catch (error) {
      console.error('Error al obtener los archivos.', error);
    }
  }

  async openAttachFile(isRentalSale: boolean) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', 'El usuario no tiene permisos para modificar sucursales. Comuníquese con el administrador del sistema', 'Atención');
      return;
    }
    this.isRentalSale = isRentalSale;
    this.nameComponent = isRentalSale ? 'Condiciones Comerciales Alquiler' : 'Condiciones Comerciales Venta';
    await this.getConditionsFiles(isRentalSale);
    this.attachFileComponent && this.attachFileComponent.show();
  }

  updateFormState() {
    const shouldDisable = !this.permissionUpdatable || this.disabledForm;
    if (shouldDisable) {
      this.formBranch.disable();
    } else {
      this.formBranch.enable();
    }
  }

  updateBranchContract(branch: BranchModel) {
    this.branchContract.predevolutionObservation = branch.predevolutionObservation;
    this.branchContract.devolutionObservation = branch.devolutionObservation;
    this.branchContract.remissionObservation = branch.remissionObservation;
    this.branchContract.icaInvoiceObservation = branch.icaInvoiceObservation;
    this.branchContract.reteIvaSaleCode = branch.reteIvaSaleCode;
    this.branchContract.cxcAccount = branch.cxcAccount;
    this.branchContract.creeDebitAccount = branch.creeDebitAccount;
    this.branchContract.creeCreditAccount = branch.creeCreditAccount;
    this.branchContract.utilizationAccount = branch.utilizationAccount;
    this.branchContract.canceledInvoiceAccount = branch.canceledInvoiceAccount;
    this.branchContract.utilityIvaContractAccount = branch.utilityIvaContractAccount;
    this.branchContract.invoiceVoucherCode = branch.invoiceVoucherCode;
    this.branchContract.debitNoteVoucherCode = branch.debitNoteVoucherCode;
    this.branchContract.creditNoteVoucherCode = branch.creditNoteVoucherCode;
    this.branchContract.cashReceiptVoucherCode = branch.cashReceiptVoucherCode;
    this.branchContract.invoiceVoucherType = branch.invoiceVoucherType;
    this.branchContract.creditNoteVoucherType = branch.creditNoteVoucherType;
    this.branchContract.debitNoteVoucherType = branch.debitNoteVoucherType;
    this.branchContract.cashReceiptVoucherType = branch.cashReceiptVoucherType;
    this.branchContract.particularConditionRental = branch.particularConditionRental;
    this.branchContract.particularConditionSale = branch.particularConditionSale;
    this.branchContract.quotationObservation = branch.quotationObservation;
    this.branchContract.swBranchStore = branch.swBranchStore;
    this.branchContract.storeId = branch.storeId;
    this.branchContract.swAttachConditions = branch.swAttachConditions;
  }

  async uploadFile(formData: any) {
    try {
      this.branchContract = await firstValueFrom(this.branchService.attachConditions(this.branchContract.branchId, this.isRentalSale, formData));
      this.validateForm(this.branchContract);
      await this.getConditionsFiles(this.isRentalSale);
      this.toastHandlerService.showToast('success', 'Se adjunto el archivo exitosamente', '');
    } catch (error) {
      console.error('Error catch:', error);
    }
  }

  validateForm(branch: BranchModel) {
    this.formBranch = this.formBuilder.group({
      predevolutionObservation: [branch.predevolutionObservation],
      devolutionObservation: [branch.devolutionObservation],
      remissionObservation: [branch.remissionObservation],
      icaInvoiceObservation: [branch.icaInvoiceObservation, [Validators.maxLength(250)]],
      reteIvaSaleCode: [branch.reteIvaSaleCode, [Validators.maxLength(10)]],
      cxcAccount: [branch.cxcAccount, [Validators.maxLength(10)]],
      creeDebitAccount: [branch.creeDebitAccount, [Validators.maxLength(10)]],
      creeCreditAccount: [branch.creeCreditAccount, [Validators.maxLength(10)]],
      utilizationAccount: [branch.utilizationAccount, [Validators.maxLength(10)]],
      canceledInvoiceAccount: [branch.canceledInvoiceAccount, [Validators.maxLength(10)]],
      utilityIvaContractAccount: [branch.utilityIvaContractAccount, [Validators.maxLength(10)]],
      invoiceVoucherCode: [branch.invoiceVoucherCode, [Validators.maxLength(6)]],
      debitNoteVoucherCode: [branch.debitNoteVoucherCode, [Validators.maxLength(6)]],
      creditNoteVoucherCode: [branch.creditNoteVoucherCode, [Validators.maxLength(6)]],
      cashReceiptVoucherCode: [branch.cashReceiptVoucherCode, [Validators.maxLength(6)]],
      invoiceVoucherType: [branch.invoiceVoucherType, [Validators.maxLength(6)]],
      creditNoteVoucherType: [branch.creditNoteVoucherType, [Validators.maxLength(6)]],
      debitNoteVoucherType: [branch.debitNoteVoucherType, [Validators.maxLength(6)]],
      cashReceiptVoucherType: [branch.cashReceiptVoucherType, [Validators.maxLength(6)]],
      particularConditionRental: [branch.particularConditionRental],
      particularConditionSale: [branch.particularConditionSale],
      quotationObservation: [branch.quotationObservation],
      storeId: [branch.storeId],
      swBranchStore: [branch.swBranchStore],
      swAttachConditions: [branch.swAttachConditions],
    });
    this.updateFormState();
  }

  validateFormControls() {
    Object.keys(this.formBranch.controls).forEach((key) => {
      const control = this.formBranch.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }

  validateStoreSelection(modelBranch: BranchModel) {
    if (!modelBranch.swBranchStore) {
      modelBranch.storeId = null;
      this.branchContract.storeId = null;
      this.formBranch.get("storeId")?.patchValue(null);
    } else if (!modelBranch.storeId) {
      this.toastHandlerService.showToast('warning', `Debe seleccionar una bodega predeterminada antes de guardar.`);
      return { isValid: false, model: modelBranch };
    }
    return { isValid: true, model: modelBranch };
  }
}
